import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { getLogoBadgeBorderColor } from '@rocco/ui/tile-professional/components/logo-badge-border';
import type { PinItemType } from '~/modules/moodboards/type';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { DetailTileAdminMenu } from '~/modules-rocco/admin/components/DetailTileAdminMenu';
import { DetailTileMedia } from './DetailTileMedia';
import { detailTileActionsFor } from './detail-tile-actions';
import type { DetailTile } from './TileCarousel';

/** Map a pin type to the admin item-type the directory admin actions expect. */
const adminItemTypeFor = (
    pinType: PinItemType | undefined,
): 'projects' | 'products' =>
    pinType === 'ProductPin' ? 'products' : 'projects';

export interface DetailTileCardProps {
    tile: DetailTile;
    /** `light` = ink on white. `dark` = light type on a dark section. */
    theme?: 'light' | 'dark';
    hideSubtitle?: boolean;
    /** Aspect/background classes for the media box. */
    mediaClassName?: string;
    /** Classes for the media `img` (e.g. `object-contain` for square studio shots). */
    imgClassName?: string;
    /**
     * The tile's rendered width as a `sizes` string (see `getImagesSizes`), so
     * the image is served through the CDN loader at the size it lands at. Opt-in:
     * without it the source rendition is served untouched, which is what every
     * existing caller does.
     */
    sizes?: string;
    /** Classes for the title/subtitle lines. */
    textClassName?: string;
    /** Override the corner logo badge's size / inset (e.g. the larger badge the
     *  professional tile carries in the network + brand-family grids). */
    logoClassName?: string;
    onTileClick?: (tileId: number) => void;
}

/**
 * One detail-page tile — media (with the Save overlay + BM admin menu when the
 * tile opts in) plus title and subtitle lines. The single tile body shared by
 * {@link TileCarousel} and the mobile tile grids.
 */
export const DetailTileCard = ({
    tile,
    theme = 'light',
    hideSubtitle = false,
    mediaClassName,
    imgClassName,
    textClassName,
    logoClassName,
    sizes,
    onTileClick,
}: DetailTileCardProps) => {
    const isDark = theme === 'dark';
    const subtitle = tile.subtitle ?? tile.professional?.name;
    // Clear-cut treatment mirrors rocco TileMedia (the prod tiles): per-image
    // `multiplied` flag → clear-cut fill token + contain + multiply; photos cover.
    const isMultiplied = Boolean(tile.images?.[0]?.multiplied);

    // Action panel config (used when the `tile_enquiry_panel` flag is on).
    const {
        enquiry: tileEnquiry,
        share: tileShare,
        tileInfo,
    } = detailTileActionsFor(tile);

    return (
        <DetailTileAdminMenu
            adminData={tile.adminData}
            itemId={tile.id}
            itemLink={tile.link}
            professionalLink={tile.professionalLink ?? ''}
            itemType={adminItemTypeFor(tile.pinType)}
        >
            <div className="relative">
                <RoccoLink
                    to={tile.link}
                    onClick={() => onTileClick?.(tile.id)}
                    className="block w-full"
                >
                    <DetailTileMedia
                        src={tile.images?.[0]?.src}
                        alt={tile.images?.[0]?.alt ?? tile.title}
                        sizes={sizes}
                        className={cn(
                            !isMultiplied && 'bg-neutral-200',
                            'max-md:aspect-[291/400] md:aspect-[446/550]',
                            mediaClassName,
                            // Last so a caller's dark section background can't win:
                            // multiply over a dark fill renders the cut-out black.
                            isMultiplied && 'bg-[var(--color-clearcut-fill)]',
                        )}
                        imgClassName={cn(
                            'max-w-none',
                            isMultiplied && 'object-contain mix-blend-multiply',
                            imgClassName,
                        )}
                        pinId={tile.pinType ? tile.id : undefined}
                        pinType={tile.pinType}
                        libraryItemId={tile.libraryItemId}
                        enableActionPanel
                        enquiry={tileEnquiry}
                        share={tileShare}
                        tileInfo={tileInfo}
                    >
                        {/* Professional logo badge (network tiles) — matches the
                        corner badge on the /professionals directory tiles. */}
                        {tile.logo ? (
                            <div
                                className={cn(
                                    'absolute flex items-center justify-center overflow-hidden rounded-full max-md:top-3 max-md:left-3 max-md:size-11 md:top-4 md:left-4 md:size-14',
                                    logoClassName,
                                )}
                                style={{
                                    backgroundColor:
                                        tile.logoBackground || '#ffffff',
                                    ...(getLogoBadgeBorderColor(
                                        tile.logoBackground,
                                    )
                                        ? {
                                              border: `1px solid ${getLogoBadgeBorderColor(tile.logoBackground)}`,
                                          }
                                        : {}),
                                }}
                            >
                                <img
                                    src={tile.logo}
                                    alt=""
                                    loading="lazy"
                                    className="size-full object-contain p-2"
                                />
                            </div>
                        ) : null}
                    </DetailTileMedia>
                    <p
                        className={cn(
                            // Figma tile (1679:75719): title Neue Haas 65 Medium,
                            // 22px, leading 1.3; media→text gap 10px (0.625rem).
                            'm-0 font-sans leading-[1.3] font-medium max-md:pt-3 max-md:text-sm md:pt-[0.625rem] md:text-[1.375rem]',
                            isDark ? 'text-white' : 'text-[#181818]',
                            textClassName,
                        )}
                    >
                        {tile.title}
                    </p>
                    {!hideSubtitle && subtitle ? (
                        <p
                            className={cn(
                                // Subtitle is the title ink at 60% — the lightest
                                // step that keeps WCAG AA contrast (4.5:1) on both
                                // themes; the Figma's 40% (1679:75721) fails at 2.5:1.
                                'm-0 font-sans leading-[1.3] max-md:text-sm md:text-[1.375rem]',
                                isDark ? 'text-white/60' : 'text-[#181818]/60',
                                textClassName,
                            )}
                        >
                            {subtitle}
                        </p>
                    ) : null}
                </RoccoLink>
                {/* Mobile actions live outside the tile link so a tap on the `•••`
                or a sheet-backdrop dismiss can never navigate the tile. */}
                {tile.pinType ? (
                    <TileMobileActionsSlot
                        pinId={tile.id}
                        pinType={tile.pinType}
                        libraryItemId={tile.libraryItemId}
                        enableActionPanel
                        enquiry={tileEnquiry}
                        share={tileShare}
                        tileInfo={tileInfo}
                    />
                ) : null}
            </div>
        </DetailTileAdminMenu>
    );
};
